import React, { useState, useEffect } from 'react'

import { Popover, Button, Typography, Box, IconButton } from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { KeyboardDatePicker } from '@material-ui/pickers'

import { DateTime } from 'luxon'

import { BaseWorkorder } from '../../../../models'
import { useTimezone } from '../../../../contexts/timezone'
import { useDateRange } from '../../../../hooks'

interface DateEditPopoverProps {
    open: boolean
    anchorEl: HTMLElement | null
    workorder: BaseWorkorder
    onClose: () => void
    onDateChange: (
        workorder: BaseWorkorder,
        startDate: string,
        endDate: string,
    ) => void
}

export const DateEditPopover: React.FC<DateEditPopoverProps> = ({
    open,
    anchorEl,
    workorder,
    onClose,
    onDateChange,
}) => {
    // Don't render if no workorder
    if (!workorder) return null

    const { currentTimezone } = useTimezone()
    const { dateRange, setRange, setStartDate, setEndDate } = useDateRange({
        rangeKey: 'DATE_EDIT_POPOVER',
        color: '#1976d2',
        useNoon: true,
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [errors, setErrors] = useState<{
        startDate?: string
        endDate?: string
    }>({})

    // Initialize dates when workorder changes
    useEffect(() => {
        if (workorder.start_date && workorder.end_date) {
            const start = DateTime.fromISO(workorder.start_date).setZone(
                currentTimezone,
            )
            const end = DateTime.fromISO(workorder.end_date).setZone(
                currentTimezone,
            )

            setRange({
                startDate: start.toJSDate(),
                endDate: end.toJSDate(),
                key: 'DATE_EDIT_POPOVER',
                color: '#1976d2',
            })
        }
    }, [workorder, currentTimezone])

    const validateDates = () => {
        const newErrors: { startDate?: string; endDate?: string } = {}

        if (!dateRange.startDate) {
            newErrors.startDate = 'Start date is required'
        }

        if (!dateRange.endDate) {
            newErrors.endDate = 'End date is required'
        }

        if (dateRange.startDate && dateRange.endDate) {
            if (dateRange.endDate < dateRange.startDate) {
                newErrors.endDate = 'End date cannot be earlier than start date'
            }
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async () => {
        if (!validateDates()) return

        setIsSubmitting(true)
        try {
            if (dateRange.startDate && dateRange.endDate) {
                // Convert to ISO strings - useNoon: true in useDateRange handles the time
                const startDateTime = dateRange.startDate.toISOString()
                const endDateTime = dateRange.endDate.toISOString()

                await onDateChange(workorder, startDateTime, endDateTime)
                onClose()
            }
        } catch (error) {
            console.error('Failed to update dates:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        // Reset to original values
        if (workorder.start_date && workorder.end_date) {
            const start = DateTime.fromISO(workorder.start_date).setZone(
                currentTimezone,
            )
            const end = DateTime.fromISO(workorder.end_date).setZone(
                currentTimezone,
            )

            setRange({
                startDate: start.toJSDate(),
                endDate: end.toJSDate(),
                key: 'DATE_EDIT_POPOVER',
                color: '#1976d2',
            })
        }
        setErrors({})
        onClose()
    }

    const handleStartDateChange = (newStartDate: Date | null) => {
        if (newStartDate) {
            setStartDate(newStartDate)
        }
        // Clear error when user starts fixing it
        if (errors.startDate) {
            setErrors((prev) => ({ ...prev, startDate: undefined }))
        }
    }

    const handleEndDateChange = (newEndDate: Date | null) => {
        if (newEndDate) {
            setEndDate(newEndDate)
        }
        // Clear error when user starts fixing it
        if (errors.endDate) {
            setErrors((prev) => ({ ...prev, endDate: undefined }))
        }
    }

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                style: {
                    padding: '20px',
                    minWidth: '350px',
                    maxWidth: '450px',
                },
            }}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                style={{ marginBottom: '20px' }}
            >
                <Typography variant="h6">Edit Dates</Typography>
                <IconButton size="small" onClick={handleClose}>
                    <Close />
                </IconButton>
            </Box>

            {/* Start Date Section */}
            <Box style={{ marginBottom: '20px' }}>
                <KeyboardDatePicker
                    value={dateRange.startDate}
                    onChange={handleStartDateChange}
                    format="MM/dd/yyyy"
                    inputVariant="outlined"
                    label="Start Date"
                    fullWidth
                    error={!!errors.startDate}
                    helperText={errors.startDate}
                    size="small"
                />
            </Box>

            {/* End Date Section */}
            <Box style={{ marginBottom: '24px' }}>
                <KeyboardDatePicker
                    value={dateRange.endDate}
                    onChange={handleEndDateChange}
                    format="MM/dd/yyyy"
                    inputVariant="outlined"
                    label="End Date"
                    fullWidth
                    error={!!errors.endDate}
                    helperText={errors.endDate}
                    size="small"
                />
            </Box>

            {/* Action Buttons */}
            <Box
                display="flex"
                style={{ gap: '12px', justifyContent: 'flex-end' }}
            >
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Updating...' : 'Update Dates'}
                </Button>
            </Box>
        </Popover>
    )
}
